import React, { useRef } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { NEW_MODE, EDIT_MODE, VIEW_MODE, COLLAPSED_MODE } from '../../common/constants';
import type { Metric, Mode, Metrics } from '../../common/types';
import { useMetricActions } from '../../controllers/metric-actions';
import { MetricForm } from './metric/editable/form/index.tsx';
import { MetricView } from './metric/metric-view/index.tsx';
import { Summary } from './summary-with-actions';
import { newMetric } from './utils';

type MetricWithSummaryProps = {
	isSelected: boolean;
	isInitialSlaPageLoad: boolean;
	isSingleMetricConfiguredForTheProject: boolean;
	canManageFields: boolean;
	metricMode: Mode;
	id: string;
	metric: Metric;
	metrics: Metrics;
	setIsInitialSlaPageLoadToFalse: () => void;
	onCancel: () => void;
};

export const MetricWithSummary = ({
	id,
	isSelected,
	metric,
	metrics,
	metricMode,
	canManageFields,
	onCancel,
	isSingleMetricConfiguredForTheProject,
	isInitialSlaPageLoad,
	setIsInitialSlaPageLoadToFalse,
}: MetricWithSummaryProps) => {
	const { name, config } = metric;
	const modeArray = [EDIT_MODE, NEW_MODE];
	const [, { updatePath }] = useMetricActions();
	const pathNotUpdated = useRef(true); // When 'updatePath' is called it causes rerenders. This hook is to prevent multiple renders

	// This method updates url to "/custom/{SLA-id}" when one metric is configured for the project and for first time SLA page load
	const updateUrlForSingleMetricInViewMode = () => {
		if (isSingleMetricConfiguredForTheProject && isInitialSlaPageLoad && pathNotUpdated.current) {
			pathNotUpdated.current = false;
			updatePath(id);
		}
	};

	const getMode = () => {
		let newMode;
		if (!modeArray.includes(metricMode)) {
			if (isSingleMetricConfiguredForTheProject && isInitialSlaPageLoad) {
				newMode = VIEW_MODE;
			} else {
				newMode = isSelected ? metricMode : COLLAPSED_MODE;
			}
		} else {
			newMode = isSelected ? metricMode : COLLAPSED_MODE;
		}
		return newMode;
	};

	const mode = getMode();
	switch (mode) {
		case EDIT_MODE:
			return (
				<MetricForm
					metric={metric}
					id={id}
					onCancel={onCancel}
					canManageFields={canManageFields}
					isNew={false}
				/>
			);
		case VIEW_MODE:
			updateUrlForSingleMetricInViewMode(); // Update the path when it is view mode, one metric is configured for the project and for first time SLA page load
			return (
				<>
					<Summary
						name={name}
						id={id}
						mode={mode}
						setIsInitialSlaPageLoadToFalse={setIsInitialSlaPageLoadToFalse}
						config={config}
						metrics={metrics}
					/>
					<MetricView metric={metric} />
				</>
			);
		default:
			return (
				<Summary
					name={name}
					id={id}
					// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Mode'.
					mode={mode}
					setIsInitialSlaPageLoadToFalse={setIsInitialSlaPageLoadToFalse}
					config={config}
					metrics={metrics}
				/>
			);
	}
};

export type NewMetricProps = {
	canManageFields: boolean;
	metricMode: Mode;
	onCancel: () => void;
};

export const NewMetric = ({ metricMode, onCancel, canManageFields }: NewMetricProps) =>
	metricMode === NEW_MODE ? (
		<Container>
			<MetricForm metric={newMetric} onCancel={onCancel} canManageFields={canManageFields} isNew />
		</Container>
	) : null;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	borderRadius: '3px',
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px 0 rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
	),
	boxSizing: 'border-box',
	border: `1px solid ${token('color.border', 'rgba(9, 30, 66, 0.08)')}`,
	margin: `${token('space.100', '8px')} 0`,
});
